import { useMemo, useRef, useState } from 'react';
import type { CustomSymbol } from '@elec/domain';
import type { Translate } from '@elec/i18n';
import {
  draftProblems,
  draftToSymbol,
  portDirFor,
  shapePath,
  type Shape,
  type SymbolDraft,
} from '@elec/symbols';
import { newId } from '@elec/domain';
import { Dialog } from './ui.tsx';

const GRID = 1.25;
const snap = (v: number) => Math.round(v / GRID) * GRID;
type Tool = 'line' | 'rect' | 'circle' | 'dot' | 'text' | 'port' | 'erase';
const TOOLS: Tool[] = ['line', 'rect', 'circle', 'dot', 'text', 'port', 'erase'];
const VIEWS = ['single-line', 'multi-line', 'control', 'position-plan'] as const;

const emptyDraft = (): SymbolDraft => ({
  name: { fr: '', en: '', nl: '' },
  tagPrefix: 'X',
  views: ['single-line', 'control'],
  width: 10,
  height: 15,
  shapes: [],
  ports: [
    { id: 'in', x: 5, y: 0, dir: 'up' },
    { id: 'out', x: 5, y: 15, dir: 'down' },
  ],
});

export function draftFromSymbol(s: CustomSymbol): SymbolDraft {
  return {
    id: s.id,
    name: { nl: '', ...s.name },
    tagPrefix: s.tagPrefix,
    views: s.views,
    width: s.width,
    height: s.height,
    shapes: (s.shapes ?? []) as Shape[],
    ports: s.ports,
  };
}

/** Bounding box of a shape, for erasing. */
function hit(s: Shape, x: number, y: number): boolean {
  const tol = 1;
  switch (s.t) {
    case 'line': {
      const dx = s.x2 - s.x1;
      const dy = s.y2 - s.y1;
      const len2 = dx * dx + dy * dy || 1;
      const u = Math.max(0, Math.min(1, ((x - s.x1) * dx + (y - s.y1) * dy) / len2));
      return Math.hypot(s.x1 + u * dx - x, s.y1 + u * dy - y) <= tol;
    }
    case 'rect':
      return x >= s.x - tol && x <= s.x + s.w + tol && y >= s.y - tol && y <= s.y + s.h + tol;
    case 'circle':
      return Math.hypot(x - s.cx, y - s.cy) <= s.r + tol;
    case 'text':
      return Math.abs(x - s.x) <= 3 && Math.abs(y - s.y) <= 2;
  }
}

/** Draw a symbol from lines, rectangles, circles, texts and connection points on a 1.25 mm grid. */
export function SymbolEditorDialog(props: {
  t: Translate;
  locale: 'fr' | 'en' | 'nl';
  initial?: CustomSymbol;
  onClose(): void;
  onSave(s: CustomSymbol): void;
}) {
  const { t } = props;
  const [d, setD] = useState<SymbolDraft>(() =>
    props.initial ? draftFromSymbol(props.initial) : emptyDraft(),
  );
  const [past, setPast] = useState<SymbolDraft[]>([]);
  const [tool, setTool] = useState<Tool>('line');
  const [text, setText] = useState('M');
  const [drag, setDrag] = useState<{ a: { x: number; y: number }; b: { x: number; y: number } } | null>(null);
  const svg = useRef<SVGSVGElement>(null);
  const scale = Math.min(22, 420 / Math.max(d.width, d.height));
  const pad = 4;
  const W = (d.width + 2 * pad) * scale;
  const H = (d.height + 2 * pad) * scale;

  const change = (next: SymbolDraft) => {
    setPast((p) => [...p.slice(-50), d]);
    setD(next);
  };
  const at = (e: React.PointerEvent) => {
    const r = svg.current!.getBoundingClientRect();
    return { x: snap((e.clientX - r.left) / scale - pad), y: snap((e.clientY - r.top) / scale - pad) };
  };
  const preview: Shape | null = useMemo(() => {
    if (!drag) return null;
    const { a, b } = drag;
    if (tool === 'line') return { t: 'line', x1: a.x, y1: a.y, x2: b.x, y2: b.y };
    if (tool === 'rect')
      return {
        t: 'rect',
        x: Math.min(a.x, b.x),
        y: Math.min(a.y, b.y),
        w: Math.abs(b.x - a.x),
        h: Math.abs(b.y - a.y),
      };
    if (tool === 'circle' || tool === 'dot')
      return {
        t: 'circle',
        cx: a.x,
        cy: a.y,
        r: Math.max(GRID / 2, Math.hypot(b.x - a.x, b.y - a.y)),
        fill: tool === 'dot',
      };
    return null;
  }, [drag, tool]);

  const down = (e: React.PointerEvent) => {
    const p = at(e);
    if (tool === 'text') {
      if (text.trim())
        change({ ...d, shapes: [...d.shapes, { t: 'text', x: p.x, y: p.y, text: text.trim(), size: 2.5 }] });
      return;
    }
    if (tool === 'port') {
      const existing = d.ports.findIndex((q) => q.x === p.x && q.y === p.y);
      if (existing >= 0) return change({ ...d, ports: d.ports.filter((_, i) => i !== existing) });
      const used = new Set(d.ports.map((q) => q.id));
      const id = ['in', 'out', ...Array.from({ length: 20 }, (_, i) => `p${i + 3}`)].find(
        (x) => !used.has(x),
      )!;
      return change({
        ...d,
        ports: [...d.ports, { id, x: p.x, y: p.y, dir: portDirFor(p.x, p.y, d.width, d.height) }],
      });
    }
    if (tool === 'erase') {
      const i = [...d.shapes].reverse().findIndex((s) => hit(s, p.x, p.y));
      if (i >= 0) change({ ...d, shapes: d.shapes.filter((_, k) => k !== d.shapes.length - 1 - i) });
      return;
    }
    (e.target as Element).setPointerCapture?.(e.pointerId);
    setDrag({ a: p, b: p });
  };
  const up = () => {
    if (preview) {
      const empty =
        (preview.t === 'line' && preview.x1 === preview.x2 && preview.y1 === preview.y2) ||
        (preview.t === 'rect' && (preview.w === 0 || preview.h === 0));
      if (!empty) change({ ...d, shapes: [...d.shapes, preview] });
    }
    setDrag(null);
  };
  const problems = draftProblems(d);
  const grid: React.ReactNode[] = [];
  for (let x = 0; x <= d.width + 1e-6; x += GRID)
    grid.push(
      <line
        key={`x${x}`}
        x1={x}
        y1={0}
        x2={x}
        y2={d.height}
        className={Math.abs(x % 5) < 1e-6 ? 'g5' : 'g'}
      />,
    );
  for (let y = 0; y <= d.height + 1e-6; y += GRID)
    grid.push(
      <line
        key={`y${y}`}
        x1={0}
        y1={y}
        x2={d.width}
        y2={y}
        className={Math.abs(y % 5) < 1e-6 ? 'g5' : 'g'}
      />,
    );
  const draw = (s: Shape, key: string | number, cls = '') =>
    s.t === 'text' ? (
      <text key={key} x={s.x} y={s.y} fontSize={s.size} textAnchor="middle" className={cls}>
        {s.text}
      </text>
    ) : (
      <path key={key} d={shapePath(s)} className={`${cls}${s.t === 'circle' && s.fill ? ' filled' : ''}`} />
    );
  const size = (k: 'width' | 'height', v: number) => {
    const nd = { ...d, [k]: Math.max(2.5, snap(v)) };
    change(nd);
  };

  return (
    <Dialog
      title={props.initial ? t('sym.editTitle') : t('sym.newTitle')}
      wide
      onClose={props.onClose}
      actions={
        <>
          <span className="muted small grow">
            {problems.length
              ? t('sym.problems', {
                  list: problems.map((p) => t(`sym.problem.${p.split(':')[0]}` as never)).join(', '),
                })
              : ''}
          </span>
          <button onClick={props.onClose}>{t('dialog.cancel')}</button>
          <button
            className="primary"
            disabled={problems.length > 0}
            onClick={() => props.onSave(draftToSymbol(d, newId))}
            data-testid="sym-save"
          >
            {t('sym.save')}
          </button>
        </>
      }
    >
      <div className="sym-editor">
        <div className="sym-form">
          <label>
            {t('sym.nameFr')}
            <input
              value={d.name.fr}
              onChange={(e) => setD({ ...d, name: { ...d.name, fr: e.target.value } })}
              data-testid="sym-name-fr"
            />
          </label>
          <label>
            {t('sym.nameEn')}
            <input
              value={d.name.en}
              onChange={(e) => setD({ ...d, name: { ...d.name, en: e.target.value } })}
            />
          </label>
          <label>
            {t('sym.nameNl')}
            <input
              value={d.name.nl}
              onChange={(e) => setD({ ...d, name: { ...d.name, nl: e.target.value } })}
            />
          </label>
          <label>
            {t('sym.tagPrefix')}
            <input
              value={d.tagPrefix}
              maxLength={3}
              onChange={(e) => setD({ ...d, tagPrefix: e.target.value.toUpperCase() })}
            />
          </label>
          <div className="row2">
            <label>
              {t('sym.width')}
              <input
                type="number"
                step={GRID}
                min={2.5}
                value={d.width}
                onChange={(e) => size('width', Number(e.target.value))}
              />
            </label>
            <label>
              {t('sym.height')}
              <input
                type="number"
                step={GRID}
                min={2.5}
                value={d.height}
                onChange={(e) => size('height', Number(e.target.value))}
              />
            </label>
          </div>
          <fieldset>
            <legend>{t('sym.views')}</legend>
            {VIEWS.map((v) => (
              <label key={v} className="check">
                <input
                  type="checkbox"
                  checked={d.views.includes(v)}
                  onChange={(e) =>
                    setD({ ...d, views: e.target.checked ? [...d.views, v] : d.views.filter((x) => x !== v) })
                  }
                />
                {t(`sheetKind.${v}`)}
              </label>
            ))}
          </fieldset>
          <p className="small muted">{t('sym.hint')}</p>
        </div>
        <div className="sym-canvas">
          <div className="sym-tools" role="toolbar">
            {TOOLS.map((k) => (
              <button
                key={k}
                className={tool === k ? 'active' : ''}
                onClick={() => setTool(k)}
                data-testid={`sym-tool-${k}`}
              >
                {t(`sym.tool.${k}` as never)}
              </button>
            ))}
            {tool === 'text' && (
              <input
                value={text}
                onChange={(e) => setText(e.target.value)}
                style={{ width: 70 }}
                aria-label={t('sym.tool.text')}
              />
            )}
            <button
              onClick={() => past.length && (setD(past.at(-1)!), setPast(past.slice(0, -1)))}
              disabled={!past.length}
            >
              {t('menu.undo')}
            </button>
          </div>
          <svg
            ref={svg}
            width={W}
            height={H}
            className="sym-svg"
            onPointerDown={down}
            onPointerMove={(e) => drag && setDrag({ ...drag, b: at(e) })}
            onPointerUp={up}
            data-testid="sym-canvas"
          >
            <g transform={`scale(${scale}) translate(${pad} ${pad})`}>
              <rect x={0} y={0} width={d.width} height={d.height} className="frame" />
              {grid}
              {d.shapes.map((s, i) => draw(s, i))}
              {preview && draw(preview, 'pv', 'preview')}
              {d.ports.map((p) => (
                <g key={p.id} className="port">
                  <circle cx={p.x} cy={p.y} r={0.5} />
                  <text x={p.x + 1} y={p.y - 0.6} fontSize={1.1}>
                    {p.id}
                  </text>
                </g>
              ))}
            </g>
          </svg>
        </div>
      </div>
    </Dialog>
  );
}
